import React, { FC, useMemo } from 'react';
import Typography from 'components/commercetools-ui/atoms/typography';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import useI18n from 'helpers/hooks/useI18n';
import useCostsData from '../hooks/useCostsData';
import { CostRef, CostsProps } from '../types';

const Costs: FC<CostsProps> = ({
  className = '',
  order,
  dataReference = 'cart',
  subCostsContainerClassName,
  subCostClassName,
  selectedLocalShippingMethod,
  totalAmountClassName,
  isCheckoutPage = false,
  // TODO: Done as part of ECOM-340. Need to be revisited properly after MVP.
  isShippingStateCompleted,
}) => {
  const { locale } = useI18n();
  const { loading, costsToRender, total } = useCostsData({
    dataReference,
    order,
    selectedLocalShippingMethod,
    isCheckoutPage,
  });
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });

  const totalAmountClassNames = useClassNames([
    'flex justify-between items-center body-text text-lg font-medium',
    {
      'pt-18 border-t-2 border-stroke-primary': !isCheckoutPage,
    },
    totalAmountClassName,
  ]);

  const subCostsContainerClassNames = useClassNames(['flex flex-col gap-8 py-16', subCostsContainerClassName]);
  const subCostsClassNames = useClassNames([
    'flex items-center justify-between capitalize text-primary',
    subCostClassName,
  ]);

  const taxRelatedKeys = ['totalNoTax', 'tax'];
  // TODO: Done as part of ECOM-340. Need to be revisited properly after MVP.
  // Redefinition of costs so we get access to the subtotal individually.
  const { regularCosts, taxCosts, subtotal, discount } = useMemo(() => {
    const regularCosts: CostRef[] = [];
    const taxCosts: CostRef[] = [];
    let subtotal: CostRef | undefined = undefined;
    let discount: CostRef | undefined = undefined;
    for (const costToRender of costsToRender) {
      if (!taxRelatedKeys.includes(costToRender.key)) regularCosts.push(costToRender);
      else taxCosts.push(costToRender);
      if (costToRender.key === 'subtotal') subtotal = costToRender;
      else if (costToRender.key === 'discount') discount = costToRender;
    }
    return { regularCosts, taxCosts, subtotal, discount };
  }, [costsToRender]);

  // TODO: Done as part of ECOM-340. Need to be revisited properly after MVP.
  // Use subtotal if we haven't passed the Shipping Step in Checkout page.
  // Add shipping only if the local shipping method is available (case only possible in Cart page)
  const totalValue = useMemo(() => {
    // Use total if we have passed the shipping state or if we're displaying Order information (Thank you page)
    if ((isCheckoutPage && isShippingStateCompleted) || dataReference === 'order') return total.value;
    if (subtotal) {
      /* Only use subtotal and shipping in Cart page since we don't display taxes */
      const value = structuredClone(subtotal.value);

      if (selectedLocalShippingMethod?.estimatedCostNumericCentAmount !== undefined && value.centAmount !== undefined)
        value.centAmount += selectedLocalShippingMethod?.estimatedCostNumericCentAmount ?? 0;

      if (discount && value.centAmount !== undefined) value.centAmount -= discount.value.centAmount ?? 0;
      return value;
    }
  }, [subtotal, isCheckoutPage, isShippingStateCompleted]);

  return (
    <div
      className={`flex flex-col ${(isCheckoutPage && isShippingStateCompleted) || dataReference === 'order' ? 'divide-y-2 border-stroke-primary' : ''} ${className}`}
    >
      <div className={subCostsContainerClassNames}>
        {regularCosts.map(({ key, label, value }) => {
          // TODO: Add conditional if as part of ECOM-340. Need to be revisited properly after MVP.
          // Ignore display of shipping if not in Checkout page && if not shipping state completed
          // Display everything if displaying data from Order instead.
          if (
            dataReference === 'cart' &&
            ((!isCheckoutPage && key === 'shipping' && !selectedLocalShippingMethod) ||
              (isCheckoutPage && !isShippingStateCompleted && key === 'shipping'))
          )
            return null;
          return (
            !!value?.centAmount &&
            value.centAmount > 0 && (
              <div key={key} className={subCostsClassNames}>
                <Typography asSkeleton={loading}>{label}</Typography>
                <Typography className="font-medium" asSkeleton={loading}>
                  {`${key === 'discount' ? '-' : ''}${CurrencyHelpers.formatForCurrency(value, locale)}`}
                </Typography>
              </div>
            )
          );
        })}
      </div>
      {/* TODO: Done as part of ECOM-340. Need to be revisited properly after MVP. */}
      {/* Ignore display of taxes until passing shipping state */}
      {((isCheckoutPage && isShippingStateCompleted) || dataReference === 'order') && (
        <div className={subCostsContainerClassNames}>
          {taxCosts.map(({ key, label, value }) => {
            if (key === 'totalNoTax' && !isShippingStateCompleted && dataReference === 'cart') return;
            return (
              !!value?.centAmount &&
              value.centAmount > 0 && (
                <div key={key} className={subCostsClassNames}>
                  <Typography asSkeleton={loading}>{label}</Typography>
                  <Typography className="font-medium" asSkeleton={loading}>
                    {CurrencyHelpers.formatForCurrency(value, locale)}
                  </Typography>
                </div>
              )
            );
          })}
        </div>
      )}

      <div className={totalAmountClassNames}>
        <div className="flex flex-col">
          <Typography asSkeleton={loading}>{total.label}</Typography>
          {!isCheckoutPage && dataReference !== 'order' && (
            <Typography asSkeleton={loading} className="body-text-x-small text-tertiary font-normal">
              {formatCartMessage({
                id: 'exclTaxes',
                defaultMessage: '(excl. taxes)',
              })}
            </Typography>
          )}
        </div>
        <Typography asSkeleton={loading} className="font-bold">
          {totalValue && CurrencyHelpers.formatForCurrency(totalValue, locale)}
        </Typography>
      </div>
    </div>
  );
};

export default Costs;
